import {
  Icon,
  HStack,
  Text,
  Stack,
  Grid,
  GridItem,
  Collapse,
  Box,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import React, { useState } from 'react';
import { NEARBY } from '@habity/constants';
import { NestedHome, NearbyResult } from '@habity/types';
import { getNearbyButtonIcon } from '~/constants';
import { NEARBY_DISTANCE_THRESHOLD } from '~/lib/homes/profile/nearbyConstants';
import { deduplicateNearbyItems } from '~/lib/homes/profile/nearbyHelpers';
import { ShowHideButton } from '~/components/ui/Buttons';
import { useAppStore } from '~/store/useAppStore';
import { useUser } from '~/hooks/useUser';
import { isBrokerOrAdmin } from '~/lib/roles';

export const Nearby = ({ home }: { home: NestedHome }) => {
  const [showAll, setShowAll] = useState(false);
  const isMobile = useAppStore((state) => state.isMobile);
  const { nearby_json, address } = home || {};
  const { city, zip } = address || {};
  const { user } = useUser();

  if (!nearby_json || nearby_json.length === 0) return null;

  // Get valid nearby types from constants
  const validTypes = new Set(NEARBY.map(n => n.key));

  // Filter items with valid types and calculate min distance
  const validItems = nearby_json.filter(item =>
    item.type && validTypes.has(item.type) && item.name
  );

  if (validItems.length === 0) return null;

  // Deduplicate items by place_id and name (keep the closest one for each unique location)
  const deduplicatedItems = deduplicateNearbyItems(validItems);

  // Calculate minimum distance to determine filtering
  const minDistance = Math.min(...deduplicatedItems.map(item => item.distance));
  const showOnlyCloseItems = minDistance < NEARBY_DISTANCE_THRESHOLD;

  // Filter by distance if needed
  const filteredItems = showOnlyCloseItems
    ? deduplicatedItems.filter(item => item.distance < NEARBY_DISTANCE_THRESHOLD)
    : deduplicatedItems;

  // Sort by distance
  const sortedItems = [...filteredItems].sort((a, b) => a.distance - b.distance);

  // Get type info for each item
  const nearbyTypeMap = new Map(NEARBY.map(n => [n.key, n]));

  // Determine if we should show collapse (3 on mobile, 5 on desktop)
  const initialLimit = isMobile ? 3 : 5;
  const hasMany = sortedItems.length > initialLimit;
  const itemsToShow = hasMany && !showAll ? sortedItems.slice(0, initialLimit) : sortedItems;

  const hideSensitiveDetails = home.status.name === "premarket" && !isBrokerOrAdmin(user);

  return (
    <Stack>
      <Collapse
        startingHeight={hasMany ? (isMobile ? 180 : 300) : 0}
        in={hasMany ? showAll : true}
      >
        <Grid
          py="4"
          maxW="600px"
          gridTemplateColumns={hideSensitiveDetails ? "120px auto" : "120px 50px auto"}
          rowGap="10"
          columnGap="10"
        >
          {itemsToShow.map((item: NearbyResult, index: number) => {
            const nearbyType = nearbyTypeMap.get(item.type);
            const { name, address: itemAddress, distance } = item;

            // Round to nearest 5 meters
            const roundedDistance = Math.round(distance / 5) * 5;

            return (
              <React.Fragment key={index}>
                <GridItem>
                  <HStack spacing="2">
                    <Icon as={getNearbyButtonIcon(item.type)} boxSize="5" />
                    <Text size="sm" fontWeight="medium">
                      {nearbyType?.title || item.type}
                    </Text>
                  </HStack>
                </GridItem>
                {!hideSensitiveDetails && (
                  <GridItem fontSize="sm">
                    {roundedDistance} m
                  </GridItem>
                )}
                <GridItem fontSize="sm" color="blackAlpha.700">
                  {hideSensitiveDetails ? (
                    name
                  ) : (
                    <Link
                      href={`http://maps.google.com/maps?q=${itemAddress?.name || name}+${itemAddress?.postcode || zip || ''}+${itemAddress?.city || city || ''}`}
                      target="_blank"
                    >
                      {name}
                    </Link>
                  )}
                </GridItem>
              </React.Fragment>
            );
          })}
        </Grid>
      </Collapse>

      {/* Toggle show all */}
      {hasMany && (
        <Box ml="-12px" mt="2">
          <ShowHideButton
            variant="plain"
            show={showAll}
            onClick={() => setShowAll(!showAll)}
            count={sortedItems.length}
          />
        </Box>
      )}
    </Stack>
  );
};
